import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import {
  RdsButtonModule,
  RdsFormFieldModule,
  RdsIconsModule,
  RdsProgressBarModule,
} from '@rds/angular-components';
import { AssistantHandoverService } from '../../services/assistant-handover.service';
import { AssistantService } from '../../services/assistant.service';
import { AnalyticsService } from '../../services/analytics.service';
import { OfferingsService } from '../../services/offerings.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { AssistantMessage } from '../../models/assistant.model';
import { Offering } from '../../models/offering';
import { MarkdownPipe } from '../../pipes/markdown.pipe';
import { SparkleIconComponent } from '../../components/shared/sparkle-icon/sparkle-icon.component';
import { PromptChipsComponent } from '../../components/shared/prompt-chips/prompt-chips.component';
import { FeatureFlagDirective } from '../../directives/feature-flag.directive';
import { OfferingLinkDirective } from '../../directives/offering-link.directive';
import { ASSISTANT_ACTIONS_FLAG } from '../../constants/assistant.constants';
import { downloadTextFile } from '../../util/download-file.util';
import {
  AssistantCta,
  SplitAnswer,
  splitCallToActions,
} from '../../util/assistant-cta.util';
import {
  findMentionedOffering,
  offeringLinkMap,
  OfferingLinkMap,
} from '../../util/offering-mention.util';
import {
  buildOfferingMarkdown,
  offeringMarkdownFileName,
} from '../../util/offering-markdown.util';

/**
 * Normalise a router-bound query param.
 *
 * `withComponentInputBinding()` writes `undefined` for a param that is absent
 * from the URL, overriding the input's declared default. Without this, opening
 * `/assistant?q=hello` — a link with no `context` — threw on `.trim()` and the
 * effect died before it could send anything, so the assistant just sat there.
 */
function fromQueryParam(value: string | undefined): string {
  return value ?? '';
}

// How long the copy button keeps saying "Copied" before its accessible name
// flips back. Two seconds is enough for a screen-reader to announce the change
// once without lingering past what the user still perceives as feedback.
const COPIED_LABEL_RESET_MS = 2000;

// Friendly, user-facing labels for the backend tool names.
const TOOL_LABELS: Record<string, string> = {
  search_offerings: 'Searching offerings…',
  get_offering: 'Fetching offering details…',
};

@Component({
  selector: 'inexp-assistant-page',
  imports: [
    FormsModule,
    RdsButtonModule,
    RdsFormFieldModule,
    RdsIconsModule,
    RdsProgressBarModule,
    MarkdownPipe,
    SparkleIconComponent,
    PromptChipsComponent,
    FeatureFlagDirective,
    OfferingLinkDirective,
  ],
  templateUrl: './assistant-page.component.html',
  styleUrl: './assistant-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantPageComponent {
  /** Exposed so the template gates on the same constant the code reads. */
  protected readonly ACTIONS_FLAG = ASSISTANT_ACTIONS_FLAG;

  private readonly _assistant = inject(AssistantService);
  private readonly _analytics = inject(AnalyticsService);
  private readonly _offerings = inject(OfferingsService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _location = inject(Location);
  private readonly _router = inject(Router);

  /**
   * Opening question, from `?q=` — bound by the router's component input
   * binding. Set by the assistant widget on the search-results page so a
   * follow-up arrives already asked rather than dropping the user on an empty
   * composer.
   */
  readonly q = input('', { transform: fromQueryParam });

  /**
   * What the user was looking at when they asked — `the "Grafana Cloud"
   * offering`, `the search for "observability"`. Folded into the opening prompt
   * rather than hidden in a system message: the user should see what was asked
   * on their behalf.
   *
   * Without it a follow-up arrives as a bare question and the assistant has no
   * idea what it refers to, which is exactly how context used to be lost on the
   * way from the results page.
   */
  readonly context = input('', { transform: fromQueryParam });

  /** Index of the answer just copied, so its button can say so. */
  readonly copiedIndex = signal<number | null>(null);

  // The confirmed transcript of completed turns.
  readonly messages = signal<AssistantMessage[]>([]);
  // The assistant reply currently streaming in (empty when idle).
  readonly streamingText = signal('');
  // Label of the tool the agent is currently running, if any.
  readonly activeTool = signal<string | null>(null);
  readonly isStreaming = signal(false);
  readonly errorMessage = signal<string | null>(null);
  // The current draft in the composer input.
  readonly draft = signal('');
  /**
   * The one line the `role="status"` region carries.
   *
   * Driven explicitly rather than derived from `isStreaming()`, because
   * stopping has something of its own to say. `stop()` commits the partial
   * answer to `messages()` and moves focus in the same tick, and a focus change
   * cancels pending polite announcements — so the log's announcement of that
   * answer is likely dropped, and an empty status region would leave the user
   * with no confirmation that anything happened at all (WCAG 4.1.3).
   */
  readonly statusMessage = signal('');

  /** True on an empty chat, where the greeting stands in for the transcript. */
  readonly isGreeting = computed(
    () => this.messages().length === 0 && !this.isStreaming(),
  );

  private readonly _transcript =
    viewChild<ElementRef<HTMLElement>>('transcript');
  private readonly _composer =
    viewChild<ElementRef<HTMLInputElement>>('composer');
  private _streamSub?: Subscription;
  /** Guards the opening `?q=` send so it fires once, not on every effect pass. */
  private _seeded = false;
  /** The same guard for the arrival focus, which must not fire twice either. */
  private _focusedOnArrival = false;
  private _copiedReset?: ReturnType<typeof setTimeout>;
  /**
   * The prompt currently being answered, kept so `stop()` can hand it back.
   * Empty whenever nothing is in flight.
   */
  private _inFlightPrompt = '';
  /** The suggestions the in-flight send displaced, for `stop()` to restore. */
  private _interruptedFollowUps: readonly string[] = [];

  constructor() {
    // Pick up the exchange a widget handed over, before anything is sent.
    //
    // Synchronously in the constructor, not in an effect: `send()` captures
    // `messages()` as the history it posts, so this has to be in place before
    // the seeding effect below runs or the opening question would go out with
    // the empty history that lost the context in the first place.
    //
    // Taking it also clears it, so coming back to `/assistant` later by another
    // route opens an empty chat rather than resurrecting this one.
    const carried = inject(AssistantHandoverService).take();
    if (carried.length) {
      this.messages.set(carried);
    }

    // Send the opening question from `?q=`. `send()` reads `messages()` and
    // `isStreaming()` and writes both, so it runs untracked — otherwise this
    // effect would depend on its own writes and re-enter.
    effect(() => {
      const question = this.q().trim();
      const subject = this.context().trim();
      if (!question || this._seeded) {
        return;
      }
      this._seeded = true;
      // The `Regarding …` frame exists to tell the agent what a bare follow-up
      // refers to. With a carried transcript it has the real thing, and the
      // frame would only make the reader's own question read as though someone
      // else had written it.
      const opening =
        subject && !carried.length ? `Regarding ${subject}: ${question}` : question;
      untracked(() => this.send(opening));
    });

    // Arriving with nothing asked — the header button, or "Ask Panorama" with
    // an empty box — puts focus straight on the composer. Typing is the only
    // thing to do on an empty chat, so leaving focus on `<body>` just made a
    // keyboard user tab through the whole header to reach the one control that
    // matters. The caret lands below the greeting, which stays reachable by
    // heading navigation or Shift+Tab — a live region would not announce it
    // either way, since it is initial content rather than a mutation.
    //
    // At most once per component instance: the router reuses this one across
    // query-param changes, and pulling the caret back mid-read would be worse
    // than never having moved it. An arrival carrying `?q=` is left alone here
    // — the seeding effect above sends it, and `send()` owns focus from there.
    effect(() => {
      if (this.q().trim() || this._focusedOnArrival) {
        return;
      }
      this._focusedOnArrival = true;
      // Untracked for the same reason as the seeding effect: `_returnFocus()`
      // only defers a signal read today, but this stops that becoming a
      // dependency if it ever reads one synchronously.
      untracked(() => this._returnFocus());
    });

    // Keep the transcript scrolled to the latest content as it streams in.
    effect(() => {
      // Read the signals so the effect re-runs when they change.
      this.messages();
      this.streamingText();
      this.activeTool();
      const el = this._transcript()?.nativeElement;
      if (el) {
        requestAnimationFrame(() => {
          // Answers now contain links and follow-up chips, so a reader can be
          // focused inside an earlier turn while a later one streams. Scrolling
          // to the bottom on every token would drag what they are on out of
          // view (WCAG 2.4.11).
          const active = document.activeElement;
          if (active && active !== el && el.contains(active)) {
            return;
          }
          el.scrollTop = el.scrollHeight;
        });
      }
    });
  }

  /** Send the current draft (or a supplied prompt) to the assistant. */
  send(prompt = this.draft()): void {
    const text = prompt.trim();
    if (!text || this.isStreaming()) {
      return;
    }

    // History is every completed turn before this prompt.
    const history = this.messages();
    this.messages.set([...history, { role: 'user', content: text }]);
    this._inFlightPrompt = text;
    this.draft.set('');
    this.streamingText.set('');
    this.activeTool.set(null);
    this.errorMessage.set(null);
    this.isStreaming.set(true);
    this.statusMessage.set('Generating an answer…');
    // The suggestions belonged to the answer that is no longer the newest one.
    // Cleared on the way out rather than when the next batch arrives, so a row
    // of chips never sits under a question that has already moved on. Kept, not
    // dropped: `stop()` undoes this send, and the suggestions under the answer
    // the reader was looking at are part of what it puts back.
    this._interruptedFollowUps = this.followUps();
    this.followUps.set([]);

    // No focus call here, and this is measured rather than argued. A
    // `_returnFocus()` used to sit on this line and could not work: the composer
    // is `[disabled]="isStreaming()"` and that was just set true, so by the time
    // the deferred focus runs the input refuses it. Probed directly —
    // `input.matches(':disabled')` is true and an explicit `.focus()` leaves
    // `document.activeElement` on `<body>`. It reads as working because
    // `NgModel` applies `disabled` on a microtask, so `.disabled` is still
    // `false` immediately after a change-detection pass.
    //
    // Worse than useless: the queued callback carries an intent from before the
    // turn, so if it runs late it pulls focus off wherever the reader has moved
    // since. `_finalizeReply()` restores focus when the answer lands, which is
    // the first moment the composer can take it.

    const traceId = crypto?.randomUUID?.();

    this._streamSub = this._assistant
      .streamChat(text, history, traceId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (event) => {
          switch (event.type) {
            case 'token':
              this.streamingText.update((t) => t + event.text);
              break;
            case 'tool':
              this.activeTool.set(TOOL_LABELS[event.name] ?? event.name);
              break;
            case 'done': {
              // Only where the turn actually produced an answer to hang them
              // off. `_finalizeReply()` commits nothing when no text arrived —
              // a turn that ran tools and said nothing, or a model that wrote
              // only the suggestions — and then `lastAssistantIndex()` still
              // points at the PREVIOUS answer, so the row would render under an
              // answer it has nothing to do with.
              const answered = !!this.streamingText().trim();
              this._finalizeReply();
              if (answered) {
                this.followUps.set(event.followUps);
              }
              break;
            }
            case 'error':
              this.errorMessage.set(event.error);
              this._finalizeReply();
              break;
          }
        },
        error: (err: unknown) => {
          this.errorMessage.set(
            err instanceof Error && err.message
              ? err.message
              : 'Panorama AI is unavailable. Please try again.',
          );
          this._finalizeReply();
        },
      });
  }

  /**
   * Stop an in-progress stream, keeping whatever text arrived so far.
   *
   * The interrupted prompt goes back into the composer. Stopping usually means
   * the question was wrong, not that the user is done — leaving the field empty
   * made them retype it from scratch just to change a word.
   */
  stop(): void {
    const interrupted = this._inFlightPrompt;
    // Read before `_finalizeReply()` clears it: whether any of the answer
    // survives decides what the transcript should be left holding.
    const answered = !!this.streamingText().trim();

    this._streamSub?.unsubscribe();
    this._finalizeReply();

    if (interrupted) {
      // Nothing came back, so the question leaves the transcript as well as
      // returning to the composer. Leaving it would show the user their own
      // abandoned turn above the same question re-typed below — and would send
      // the backend a history ending on a user turn, so the resend would arrive
      // as two user messages in a row with no reply between them.
      if (!answered) {
        this.messages.update((m) => {
          const last = m[m.length - 1];
          return last?.role === 'user' && last.content === interrupted
            ? m.slice(0, -1)
            : m;
        });
        // The transcript is back to what it was, so the suggestions that sat
        // under its last answer belong there again. Without this, stopping
        // before the first token silently costs the reader the row they were
        // looking at a moment ago.
        this.followUps.set(this._interruptedFollowUps);
      }
      this.draft.set(interrupted);
      this.statusMessage.set('Stopped. Your question is back in the message box.');
    }

    this._returnFocus();
  }

  /**
   * Put focus back on the composer.
   *
   * Stop, Regenerate and the follow-up chips all remove the very control the
   * user activated, which drops focus to `<body>` and loses a screen reader's
   * place (WCAG 2.4.3). The composer is where the next action is anyway.
   *
   * Only called from where the composer can actually take focus: `stop()`, the
   * arrival effect, and `_finalizeReply()`. While a turn is in flight it is
   * disabled and this would do nothing — see the note in `send()`.
   */
  private _returnFocus(): void {
    requestAnimationFrame(() => this._composer()?.nativeElement.focus());
  }

  /**
   * Put focus back only if the turn left it nowhere.
   *
   * The test is made inside the frame rather than before scheduling it, which
   * is the whole point: between a turn ending and the frame running, a reader
   * can move focus deliberately — onto Stop, a link in an answer, Leave — and a
   * decision taken earlier would pull it back off them a frame later. Scheduling
   * on the intent and acting on the stale version of it is how the first
   * attempt at this passed its own test by accident.
   */
  private _restoreLostFocus(): void {
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && active !== document.body) {
        return;
      }
      this._composer()?.nativeElement.focus();
    });
  }

  /** Index of the newest assistant turn, or -1 when there is none. */
  readonly lastAssistantIndex = computed(() => {
    const messages = this.messages();
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        return i;
      }
    }
    return -1;
  });

  /**
   * The one switch for everything this story adds.
   *
   * The follow-up chips are gated by `*inexpFeatureFlag` in the template, but
   * the inline links are not template structure — they are produced inside the
   * markdown render. Reading the flag here too means the whole feature goes off
   * together, rather than leaving half of it permanently on.
   */
  private readonly _actionsEnabled = inject(FeatureFlagService).createFlagSignal(
    ASSISTANT_ACTIONS_FLAG,
  );

  /**
   * The offering each answer was about, index-aligned with `messages()`.
   *
   * Read from the **question** that produced the answer, walking back past
   * earlier turns to the nearest one. Deliberately not from the answer text:
   * this drives the factsheet download, and over-matching a question costs a
   * file about the wrong thing.
   */
  readonly offeringPerMessage = computed<(Offering | undefined)[]>(() => {
    const catalogue = this._offerings.offerings();
    const messages = this.messages();

    return messages.map((message, index) => {
      if (message.role !== 'assistant') {
        return undefined;
      }
      let question = '';
      for (let i = index - 1; i >= 0; i--) {
        if (messages[i].role === 'user') {
          question = messages[i].content;
          break;
        }
      }
      return findMentionedOffering(question, catalogue);
    });
  });

  /**
   * Download the factsheet for the offering a turn was about.
   *
   * The catalogue summary the match came from is too thin for the document, so
   * the full record is fetched on click — the same call and the same renderer
   * the offering page uses, which is what keeps the two files identical. The
   * footer points at the offering's own page rather than at this conversation,
   * since that is the durable address of what the file describes.
   */
  downloadOffering(offering: Offering): void {
    this._offerings
      .fetchPublishedOffering(offering.slug)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((record) => {
        if (!record) {
          return;
        }
        downloadTextFile(
          buildOfferingMarkdown(record, {
            sourceUrl: `${window.location.origin}/offerings/${offering.slug}`,
          }),
          offeringMarkdownFileName(record),
          'text/markdown;charset=utf-8',
        );
      });
  }

  /**
   * Offering names to link inside each turn, index-aligned with `messages()`.
   *
   * Read from the **answer**, which is the opposite of the rule above and
   * deliberate: over-matching costs a link nobody clicks, where over-matching a
   * button costs a wrong action. A link on "Panorama" that opens the Panorama
   * offering is simply correct.
   *
   * Only completed turns get a map. The streaming bubble is left plain — the
   * match runs against the whole catalogue, so doing it per token would be the
   * most expensive thing on the page, and a half-arrived name flickering into a
   * link reads as a glitch. That bubble is `inert` anyway, so a link in it
   * could not be used.
   */
  readonly offeringLinksPerMessage = computed<(OfferingLinkMap | undefined)[]>(() => {
    if (!this._actionsEnabled()) {
      return [];
    }
    const catalog = this._offerings.offerings();
    return this.messages().map((message, index) =>
      message.role === 'assistant'
        ? this._linksFor(this.answerPerMessage()[index]?.body ?? '', catalog)
        : undefined,
    );
  });

  /**
   * Each answer split into the prose to render and the actions to offer,
   * index-aligned with `messages()`.
   *
   * A call to action is lifted out of the answer's own text, so unlike the
   * follow-up questions it stays with **every** turn that has one rather than
   * only the newest: dropping it from an older answer would delete something
   * the reader could see a moment ago.
   *
   * With the flag off the body is the original, untouched. Stripping the line
   * while rendering no control in its place would remove the route outright,
   * which is the one outcome worse than leaving it in the sentence.
   *
   * Deliberately NOT cached, unlike `_linkCache` below. That cache exists
   * because an `OfferingLinkMap` is an object and `MarkdownPipe` is pure, so
   * identity is the only thing Angular can compare. A `body` is a string, and
   * `Object.is` on two equal strings is true however they were built — so a
   * cache here would buy one line scan per prior turn and nothing else, while
   * reading as though it were load-bearing.
   */
  readonly answerPerMessage = computed<(SplitAnswer | undefined)[]>(() => {
    const enabled = this._actionsEnabled();
    return this.messages().map((message) => {
      if (message.role !== 'assistant') {
        return undefined;
      }
      return enabled
        ? splitCallToActions(message.content)
        : { body: message.content, actions: [] };
    });
  });

  /** Record a call to action being taken. The anchor navigates on its own. */
  trackCallToAction(cta: AssistantCta): void {
    this._track('assistant call to action', cta.url);
  }

  /**
   * Link map for one answer, reused while that answer's text is unchanged.
   *
   * `MarkdownPipe` is pure and keyed on argument identity, so handing it a
   * fresh `Map` would re-parse and re-render every earlier answer on every new
   * turn. Matching is not cheap either — it compiles one regex per catalogue
   * entry — so without this the cost of a conversation is quadratic in its
   * length.
   *
   * The cache is bounded by the transcript: one entry per answer, and the
   * component is discarded when the user leaves.
   */
  private readonly _linkCache = new Map<string, OfferingLinkMap>();

  private _linksFor(content: string, catalog: readonly Offering[]): OfferingLinkMap {
    const cached = this._linkCache.get(content);
    if (cached) {
      return cached;
    }
    const map = offeringLinkMap(content, catalog);
    this._linkCache.set(content, map);
    return map;
  }

  /**
   * The questions the agent suggested after the newest answer.
   *
   * One signal rather than a map per turn, because only the newest answer
   * carries them: a chip is the next step in the conversation, and under an
   * older answer it would propose carrying on from a place the reader has
   * already left. Arrives on the `done` frame, so it lands with the finished
   * answer rather than appearing mid-stream.
   */
  readonly followUps = signal<readonly string[]>([]);

  /**
   * Spend one of the suggested questions as the reader's own turn.
   *
   * The chip carries the whole question, so what lands in the transcript is
   * exactly what they read before pressing it. `send()` clears the row — these
   * belong to the answer above them, which is no longer the newest one.
   */
  askFollowUp(prompt: string): void {
    this._track('assistant follow up', prompt);
    this.send(prompt);
  }

  /**
   * Record an action with Faro.
   *
   * `pushUserActionEvent` wraps `pushEvent`, never `startUserAction` — the
   * latter waits for the DOM to fall quiet for 100ms and never gets it on an
   * interaction that navigates and re-renders, which freezes the tab.
   */
  private _track(name: string, detail: string): void {
    this._analytics.pushUserActionEvent(name, { detail });
  }

  /** Leave the chat, back to wherever the user came from. */
  leave(): void {
    // A direct link to /assistant has nothing to go back to, and `back()` would
    // take the user out of the app entirely.
    if (typeof window !== 'undefined' && window.history.length > 1) {
      this._location.back();
      return;
    }
    this._router.navigate(['/']);
  }

  /** Put an answer on the clipboard. */
  copy(content: string, index: number): void {
    void navigator?.clipboard?.writeText(content);
    this.copiedIndex.set(index);
    // Without this the button's accessible name stays "Copied" for the rest of
    // the session, which stops being true the moment the user looks away.
    clearTimeout(this._copiedReset);
    this._copiedReset = setTimeout(() => this.copiedIndex.set(null), COPIED_LABEL_RESET_MS);
  }

  /**
   * Ask again for the newest answer.
   *
   * Drops the trailing assistant turn and replays the user prompt that produced
   * it, so the history the backend sees is the same as the first time round.
   */
  regenerate(): void {
    if (this.isStreaming()) {
      return;
    }
    const messages = this.messages();
    const lastAssistant = this.lastAssistantIndex();
    if (lastAssistant < 0) {
      return;
    }
    const lastUser = [...messages]
      .slice(0, lastAssistant)
      .reverse()
      .find((m) => m.role === 'user');
    if (!lastUser) {
      return;
    }
    // Drop the answer and the prompt; `send()` puts the prompt back.
    this.messages.set(messages.slice(0, lastAssistant - 1));
    this.copiedIndex.set(null);
    this.send(lastUser.content);
    // Focus comes back from `_finalizeReply()`, for the reason given in
    // `send()`: the Regenerate button is gone and the composer is disabled, so
    // there is nothing here that could take it yet.
  }

  /** Commit any streamed text as an assistant turn and reset streaming state. */
  private _finalizeReply(): void {
    const text = this.streamingText().trim();
    if (text) {
      this.messages.update((m) => [...m, { role: 'assistant', content: text }]);
    }
    this.streamingText.set('');
    this.activeTool.set(null);
    this.isStreaming.set(false);
    // `stop()` overwrites this with its own line straight after; on a turn that
    // finished by itself the log announces the answer, so the status region has
    // nothing left to add.
    this.statusMessage.set('');
    // Read by `stop()` before this runs; a completed turn has nothing to hand
    // back, so it is cleared either way.
    this._inFlightPrompt = '';

    // Put focus back if the turn lost it, which any send from a control that
    // does not survive the send does. The composer is disabled for the whole
    // stream, so this is the first moment anything can take focus — see the
    // note in `send()`. Most visible on a follow-up chip, where the activated
    // control is gone as well, but the same on Send and on Regenerate.
    //
    // Only from `<body>`: a reader who moved somewhere during the stream — the
    // Stop button, an earlier turn, a link in an answer — must not have the
    // caret pulled out from under them. The streaming window itself still leaves
    // focus nowhere; fixing that means the composer staying focusable while
    // disabled, which is a change to the composer and belongs with that ticket.
    this._restoreLostFocus();
  }
}
